import { useState, useEffect, useCallback } from 'react'
import { apiService } from '../services/api'
import { getErrorMessage } from '../utils/errorHandling'
import type { UploadPermissionResponse } from '../types'

interface UseUploadPermissionOptions {
  solicitudId: string
  enabled?: boolean
  refreshInterval?: number
}

interface UseUploadPermissionReturn {
  permission: UploadPermissionResponse | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  isUploadAllowed: boolean
}

/**
 * Custom hook for managing upload permissions and approval status
 */
export const useUploadPermission = ({
  solicitudId,
  enabled = true,
  refreshInterval = 30000, // 30 seconds default
}: UseUploadPermissionOptions): UseUploadPermissionReturn => {
  const [permission, setPermission] = useState<UploadPermissionResponse | null>(
    null
  )
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState<string | null>(null)

  const fetchPermission = useCallback(async () => {
    if (!enabled) {
      setPermission({
        upload_allowed: true,
        approval_status: 'approved',
      })
      setLoading(false)
      return
    }

    try {
      setError(null)
      const result = await apiService.checkUploadPermission(solicitudId)
      setPermission(result)
    } catch (err) {
      const errorMessage = getErrorMessage(err)
      setError(errorMessage)
      console.error('Error checking upload permission:', err)
    } finally {
      setLoading(false)
    }
  }, [solicitudId, enabled])

  // Initial fetch
  useEffect(() => {
    fetchPermission()
  }, [fetchPermission])

  // Set up auto-refresh for approval status
  useEffect(() => {
    if (!enabled || !refreshInterval || refreshInterval <= 0) {
      return
    }

    const intervalId = setInterval(() => {
      // Only refresh if uploads are not currently allowed
      if (permission && !permission.upload_allowed) {
        fetchPermission()
      }
    }, refreshInterval)

    return () => clearInterval(intervalId)
  }, [enabled, refreshInterval, permission, fetchPermission])

  const isUploadAllowed = permission?.upload_allowed ?? false

  return {
    permission,
    loading,
    error,
    refetch: fetchPermission,
    isUploadAllowed,
  }
}
